import React, { useMemo } from "react";
import { ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import { Order } from "src/shared/types";

type RefundType = "Cash" | "Card" | "Adj.Outstanding";

interface Props {
    order: Order | null;
    totals: any;
    reverseDiscount: number;
    setReverseDiscount: (val: number) => void;
    refundType: RefundType;
    setRefundType: (val: RefundType) => void;
}

export default function ReturnSummaryCard({
    order,
    totals,
    reverseDiscount,
    setReverseDiscount,
    refundType,
    setRefundType,
}: Props) {
    const refundOptions = useMemo(() => {
        const hasRefund = totals?.refund > 0;
        const hasAdjustment = totals?.adjusted > 0;

        if (hasRefund && !hasAdjustment) return ["Cash", "Card"];
        if (!hasRefund && hasAdjustment) return ["Adj.Outstanding"];
        if (hasRefund && hasAdjustment) return ["Cash", "Card"];
        return ["Cash"];
    }, [totals?.adjusted, totals?.refund]);

    if (!order) return null;

    // New interface: discount lives in orders[0].discount
    const discount = Number(order.orders?.[0]?.discount ?? 0);

    return (
        <div className="bg-white rounded shadow-md border border-gray-100">
            <div
                className={`${ThemeBasedColorHeader()} text-white px-4 py-1 rounded-t font-semibold text-sm`}
            >
                Return Summary
            </div>

            <div className="p-4 text-sm space-y-2">
                <div className="flex justify-between">
                    <span>Total Qty</span>
                    <span>{totals?.qty || 0}</span>
                </div>

                <div className="flex justify-between">
                    <span>Total Amount</span>
                    <span>QR {Number(totals?.amount || 0).toFixed(2)}</span>
                </div>

                {/* Reverse Discount capped at order's discount */}
                <div className="flex justify-between items-center">
                    <span>Reverse Discount</span>
                    <input
                        type="number"
                        value={reverseDiscount}
                        min={0}
                        max={discount}
                        onChange={(e) => {
                            const val = Number(e.target.value || 0);
                            if (val <= discount && val >= 0) setReverseDiscount(val);
                        }}
                        className="w-24 border px-2 py-1 rounded text-right"
                    />
                </div>

                <div className="flex justify-between bg-red-50 p-2 rounded">
                    <span className="text-red-600 font-semibold">Adj.Outstanding</span>
                    <span className="text-red-600 font-bold">
                        QR {Number(totals?.adjusted || 0).toFixed(2)}
                    </span>
                </div>

                <div className="flex justify-between bg-green-50 p-2 rounded">
                    <span className="font-semibold">Refund</span>
                    <span className="text-green-600 font-bold">
                        QR {Number(totals?.refund || 0).toFixed(2)}
                    </span>
                </div>

                <div>
                    <label className="text-xs">Refund Method</label>
                    <select
                        value={refundType}
                        onChange={(e) => setRefundType(e.target.value as RefundType)}
                        className="w-full mt-1 px-2 py-2 border rounded bg-white"
                    >
                        {refundOptions.map((opt) => (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}